import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { ModuleItemCoreComponent } from '../module-item-core/module-item-core.component';
import { Study } from '../../../../../../models/study';
import { Module } from '../../../../../../models/module';
import { ModuleItemSubType } from '../../../../../../models/module-item-sub-type';
import { ModuleItem } from '../../../../../../models/module-item';
import {
  MODULE_ITEM_DESCRIPTION_IDENTIFIER,
  MODULE_ITEM_LABEL_IDENTIFIER,
  MODULE_ITEM_QUESTION_IDENTIFIER,
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
  RANDOMIZATION_VALUES,
} from 'src/app/core/dashboard-constants';
import { StudyType } from 'src/app/models/study-type';
import { PrivilegeService } from '../../../../../services/privilege/privilege.service';
import { AppState } from '../../../../../../state';
import { selectSelectedLanguage, selectSupportedLanguages } from '../../../../../state/study/study.selectors';
import { Language } from '../../../../../../models/language';
import { ModuleItemDialogComponent } from '../../module-item-dialog.component';
import { ModuleItemAvailableAttribute } from '../../../../../../models/module-item-attribute';
import { removeWhitespaces } from '../../../../../../core/custom-form-validators';
import { StudyTranslation } from '../../../../../../models/study-translation';
import { prepareApiVideoUrl } from '../../../../helpers/module-item-helper';
import { createModuleItem, updateModuleItem } from '../../../../../state/module-item/module-item.actions';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { RoutingService } from 'src/app/dashboard/services/routing/routing.service';
import { PreviewService } from 'src/app/dashboard/services/preview/preview.service';
import {
  MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO,
  MODULE_ITEM_DEFAULT_IVY_CORRECT_COLOR,
  MODULE_ITEM_DEFAULT_IVY_DRAG_WORD_COLOR,
  MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO,
  MODULE_ITEM_DEFAULT_IVY_WRONG_COLOR,
  MODULE_ITEM_IVY_BOTTOM_ZONE,
  MODULE_ITEM_IVY_TOP_ZONE,
} from '../../../../../../core/dashboard-constants';
import { isThereEmptyTranslation } from '../../../../helpers/translation-helper';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'dashboard-ivy',
  templateUrl: './ivy.component.html',
  styleUrls: ['./ivy.component.scss'],
})
export class IvyComponent implements OnInit, OnDestroy {
  @ViewChild(ModuleItemCoreComponent, { static: true })
  dashboardModuleItemCore: ModuleItemCoreComponent;

  @Input() dialogData;

  ivyForm: FormGroup;

  selectedStudy: Study;
  selectedModule: Module;
  moduleItemSubType: ModuleItemSubType;
  selectedModuleItem: ModuleItem;
  isDuplicate: boolean;
  isEdit: boolean;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  StudyType = StudyType;
  ModuleItemAvailableAttribute = ModuleItemAvailableAttribute;

  formSubmitted = false;

  isTopZoneAudioFileToEdit: boolean;
  topZoneAudioFilePath: string;

  isBottomZoneAudioFileToEdit: boolean;
  bottomZoneAudioFilePath: string;

  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_MODULE_ITEM;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private moduleItemDialogRef: MatDialogRef<ModuleItemDialogComponent>,
    private changeDetectorRef: ChangeDetectorRef,
    public privilegeService: PrivilegeService,
    private routingService: RoutingService,
    private previewService: PreviewService,
    private toastrService: ToastrService
  ) {}

  get moduleItemCore(): AbstractControl {
    return this.ivyForm.get('moduleItemCore');
  }

  get translations(): FormArray {
    return this.ivyForm.get('translations') as FormArray;
  }

  get hideInfo(): AbstractControl {
    return this.ivyForm.get('hideInfo');
  }

  get defaultTopZoneAudio(): AbstractControl {
    return this.ivyForm.get('defaultTopZoneAudio');
  }

  get defaultBottomZoneAudio(): AbstractControl {
    return this.ivyForm.get('defaultBottomZoneAudio');
  }

  get topZoneColor(): AbstractControl {
    return this.ivyForm.get('topZoneColor');
  }

  get topZoneAudio(): AbstractControl {
    return this.ivyForm.get('topZoneAudio');
  }

  get bottomZoneColor(): AbstractControl {
    return this.ivyForm.get('bottomZoneColor');
  }

  get bottomZoneAudio(): AbstractControl {
    return this.ivyForm.get('bottomZoneAudio');
  }

  get dragWordColor(): AbstractControl {
    return this.ivyForm.get('dragWordColor');
  }

  get correctZone(): AbstractControl {
    return this.ivyForm.get('correctZone');
  }

  ngOnInit(): void {
    this.ivyForm = this.formBuilder.group({
      moduleItemCore: [],
      translations: this.formBuilder.array([]),
      hideInfo: [false],
      defaultTopZoneAudio: [false],
      defaultBottomZoneAudio: [false],
      topZoneColor: [MODULE_ITEM_DEFAULT_IVY_WRONG_COLOR],
      topZoneAudio: [''],
      bottomZoneColor: [MODULE_ITEM_DEFAULT_IVY_CORRECT_COLOR],
      bottomZoneAudio: [''],
      dragWordColor: [MODULE_ITEM_DEFAULT_IVY_DRAG_WORD_COLOR],
      correctZone: [MODULE_ITEM_IVY_BOTTOM_ZONE],
    });

    this.subscriptions.add(
      this.correctZone.valueChanges.subscribe((correctZone) => {
        if (correctZone === MODULE_ITEM_IVY_TOP_ZONE) {
          if (this.topZoneColor.value === MODULE_ITEM_DEFAULT_IVY_WRONG_COLOR) {
            this.topZoneColor.setValue(MODULE_ITEM_DEFAULT_IVY_CORRECT_COLOR);
          }
          if (this.bottomZoneColor.value === MODULE_ITEM_DEFAULT_IVY_CORRECT_COLOR) {
            this.bottomZoneColor.setValue(MODULE_ITEM_DEFAULT_IVY_WRONG_COLOR);
          }
        } else {
          if (this.topZoneColor.value === MODULE_ITEM_DEFAULT_IVY_CORRECT_COLOR) {
            this.topZoneColor.setValue(MODULE_ITEM_DEFAULT_IVY_WRONG_COLOR);
          }
          if (this.bottomZoneColor.value === MODULE_ITEM_DEFAULT_IVY_WRONG_COLOR) {
            this.bottomZoneColor.setValue(MODULE_ITEM_DEFAULT_IVY_CORRECT_COLOR);
          }
        }
        if (this.defaultTopZoneAudio.value) {
          this.previewService.setPreviewData({
            ivyElements: {
              audioVideo:
                correctZone === MODULE_ITEM_IVY_TOP_ZONE
                  ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                  : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO,
              zone: 'top',
            },
          });
        }
        if (this.defaultBottomZoneAudio.value) {
          this.previewService.setPreviewData({
            ivyElements: {
              audioVideo:
                correctZone === MODULE_ITEM_IVY_BOTTOM_ZONE
                  ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                  : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO,
              zone: 'bottom',
            },
          });
        }
      })
    );
    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((selectedLanguage) => {
        this.selectedLanguage = selectedLanguage;
        this.previewService.setPreviewData({ ivyElements: { selectedLanguage } });
        this.changeDetectorRef.detectChanges();
      })
    );
    this.subscriptions.add(
      this.defaultTopZoneAudio.valueChanges.subscribe((defaultTopZoneAudioChecked) => {
        this.previewService.setPreviewData({
          ivyElements: {
            audioVideo: defaultTopZoneAudioChecked
              ? this.correctZone.value === MODULE_ITEM_IVY_TOP_ZONE
                ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO
              : this.topZoneAudio.value || '',
            zone: 'top',
          },
        });
      })
    );
    this.subscriptions.add(
      this.defaultBottomZoneAudio.valueChanges.subscribe((defaultBottomZoneAudioChecked) => {
        this.previewService.setPreviewData({
          ivyElements: {
            audioVideo: defaultBottomZoneAudioChecked
              ? this.correctZone.value === MODULE_ITEM_IVY_BOTTOM_ZONE
                ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO
              : this.bottomZoneAudio.value || '',
            zone: 'bottom',
          },
        });
      })
    );
    this.subscriptions.add(
      this.topZoneAudio.valueChanges.subscribe((filePathValueChanges) => {
        this.previewService.setPreviewData({ ivyElements: { audioVideo: filePathValueChanges, zone: 'top' } });
      })
    );
    this.subscriptions.add(
      this.bottomZoneAudio.valueChanges.subscribe((filePathValueChanges) => {
        this.previewService.setPreviewData({ ivyElements: { audioVideo: filePathValueChanges, zone: 'bottom' } });
      })
    );

    this.subscriptions.add(
      this.ivyForm.valueChanges.subscribe((change) =>
        this.previewService.setPreviewData({
          ivyElements: { selectedLanguage: this.selectedLanguage, ...change },
        })
      )
    );

    this.selectedStudy = this.dialogData?.selectedStudy;
    this.selectedModule = this.dialogData?.selectedModule;
    this.moduleItemSubType = this.dialogData?.moduleItemSubType;
    this.selectedModuleItem = this.dialogData?.moduleItem;
    this.isDuplicate = this.dialogData?.isDuplicate;

    this.isEdit = !!this.selectedModuleItem;

    this.translations.clear();

    if (this.isEdit || this.isDuplicate) {
      if (this.supportedLanguages) {
        this.supportedLanguages.forEach((language) => this.addTranslationFields(language?.code));
      }

      if (this.selectedModuleItem?.attributes?.length) {
        const hideInfoAttribute = this.selectedModuleItem.attributes.find(
          (attribute) => attribute.attribute === ModuleItemAvailableAttribute.HIDE_INFO
        );
        if (hideInfoAttribute) {
          this.hideInfo.setValue(parseInt(hideInfoAttribute.value, 10) === 1);
        }

        const correctZoneAttribute = this.selectedModuleItem.attributes.find(
          (attribute) => attribute.attribute === ModuleItemAvailableAttribute.CORRECT_ZONE
        );
        if (correctZoneAttribute) {
          this.correctZone.setValue(correctZoneAttribute.value.toString());
        }

        const audioPaths = this.selectedModuleItem.attributes.filter(
          (att) =>
            att.attribute === ModuleItemAvailableAttribute.TOP_ZONE_AUDIO ||
            att.attribute === ModuleItemAvailableAttribute.BOTTOM_ZONE_AUDIO
        );
        if (audioPaths?.length) {
          audioPaths.forEach((audioPath) => {
            switch (audioPath.attribute) {
              case 'top_zone_audio':
                this.defaultTopZoneAudio.setValue(
                  audioPath.value ===
                    (this.correctZone.value === MODULE_ITEM_IVY_TOP_ZONE
                      ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                      : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO)
                );
                this.isTopZoneAudioFileToEdit = !this.defaultTopZoneAudio.value && !!audioPath.value;
                this.topZoneAudioFilePath = audioPath.value;
                this.topZoneAudio.setValue(this.defaultTopZoneAudio.value ? '' : this.topZoneAudioFilePath);
                this.idsForUpload = {
                  studyId: this.selectedStudy?.id,
                  moduleId: this.selectedModule?.id,
                };
                break;
              case 'bottom_zone_audio':
                this.defaultBottomZoneAudio.setValue(
                  audioPath.value ===
                    (this.correctZone.value === MODULE_ITEM_IVY_BOTTOM_ZONE
                      ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                      : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO)
                );
                this.isBottomZoneAudioFileToEdit = !this.defaultBottomZoneAudio.value && !!audioPath.value;
                this.bottomZoneAudioFilePath = audioPath.value;
                this.bottomZoneAudio.setValue(this.defaultBottomZoneAudio.value ? '' : this.bottomZoneAudioFilePath);
                this.idsForUpload = {
                  studyId: this.selectedStudy?.id,
                  moduleId: this.selectedModule?.id,
                };
                break;
            }
          });
        }
        const topZoneColorAttribute = this.selectedModuleItem.attributes.find(
          (attribute) => attribute.attribute === ModuleItemAvailableAttribute.TOP_ZONE_COLOR
        );
        if (topZoneColorAttribute) {
          this.topZoneColor.setValue(topZoneColorAttribute.value);
        }
        const bottomZoneColorAttribute = this.selectedModuleItem.attributes.find(
          (attribute) => attribute.attribute === ModuleItemAvailableAttribute.BOTTOM_ZONE_COLOR
        );
        if (bottomZoneColorAttribute) {
          this.bottomZoneColor.setValue(bottomZoneColorAttribute.value);
        }
        const dragWordColorAttribute = this.selectedModuleItem.attributes.find(
          (attribute) => attribute.attribute === ModuleItemAvailableAttribute.DRAG_WORD_COLOR
        );
        if (dragWordColorAttribute) {
          this.dragWordColor.setValue(dragWordColorAttribute.value);
        }
      }
    } else {
      this.selectedLanguage
        ? this.supportedLanguages.forEach((language) => this.addTranslationFields(language?.code))
        : this.addTranslationFields(this.selectedLanguage.code);
    }
  }

  addTranslationFields(languageCode): void {
    const topZoneGroupAttribute = this.selectedModuleItem?.attributes
      ?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.TOP_ZONE_GROUP)
      ?.translations?.find((translation) => translation?.language === languageCode);

    const topZoneWordAttribute = this.selectedModuleItem?.attributes
      ?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.TOP_ZONE_WORD)
      ?.translations?.find((translation) => translation?.language === languageCode);

    const bottomZoneGroupAttribute = this.selectedModuleItem?.attributes
      ?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.BOTTOM_ZONE_GROUP)
      ?.translations?.find((translation) => translation?.language === languageCode);

    const bottomZoneWordAttribute = this.selectedModuleItem?.attributes
      ?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.BOTTOM_ZONE_WORD)
      ?.translations?.find((translation) => translation?.language === languageCode);

    const dragWordAttribute = this.selectedModuleItem?.attributes
      ?.find((attribute) => attribute.attribute === ModuleItemAvailableAttribute.DRAG_WORD)
      ?.translations?.find((translation) => translation?.language === languageCode);

    const topZoneGroup = this.formBuilder.group({
      id: [topZoneGroupAttribute ? topZoneGroupAttribute?.id : 0],
      language: [languageCode],
      identifier: [ModuleItemAvailableAttribute.TOP_ZONE_GROUP],
      translation: [topZoneGroupAttribute ? topZoneGroupAttribute?.translation : '', [removeWhitespaces]],
    });

    const topZoneWord = this.formBuilder.group({
      id: [topZoneWordAttribute ? topZoneWordAttribute?.id : 0],
      language: [languageCode],
      identifier: [ModuleItemAvailableAttribute.TOP_ZONE_WORD],
      translation: [topZoneWordAttribute ? topZoneWordAttribute?.translation : '', [removeWhitespaces]],
    });

    const bottomZoneGroup = this.formBuilder.group({
      id: [bottomZoneGroupAttribute ? bottomZoneGroupAttribute?.id : 0],
      language: [languageCode],
      identifier: [ModuleItemAvailableAttribute.BOTTOM_ZONE_GROUP],
      translation: [bottomZoneGroupAttribute ? bottomZoneGroupAttribute?.translation : '', [removeWhitespaces]],
    });

    const bottomZoneWord = this.formBuilder.group({
      id: [bottomZoneWordAttribute ? bottomZoneWordAttribute?.id : 0],
      language: [languageCode],
      identifier: [ModuleItemAvailableAttribute.BOTTOM_ZONE_WORD],
      translation: [bottomZoneWordAttribute ? bottomZoneWordAttribute?.translation : '', [removeWhitespaces]],
    });

    const dragWord = this.formBuilder.group({
      id: [dragWordAttribute ? dragWordAttribute?.id : 0],
      language: [languageCode],
      identifier: [ModuleItemAvailableAttribute.DRAG_WORD],
      translation: [dragWordAttribute ? dragWordAttribute?.translation : '', [removeWhitespaces]],
    });

    this.translations.push(topZoneGroup);
    this.translations.push(topZoneWord);
    this.translations.push(bottomZoneGroup);
    this.translations.push(bottomZoneWord);
    this.translations.push(dragWord);
  }

  buildTranslationsAndAttributesForRequest(translations: StudyTranslation[]) {
    const translationsAndAttributes = {
      translations: [],
      attributes: [],
    };

    let labelTranslation = null;

    translations?.forEach((translation) => {
      if (
        translation.identifier === ModuleItemAvailableAttribute.TOP_ZONE_GROUP ||
        translation.identifier === ModuleItemAvailableAttribute.TOP_ZONE_WORD ||
        translation.identifier === ModuleItemAvailableAttribute.BOTTOM_ZONE_WORD ||
        translation.identifier === ModuleItemAvailableAttribute.BOTTOM_ZONE_GROUP ||
        translation.identifier === ModuleItemAvailableAttribute.DRAG_WORD
      ) {
        translationsAndAttributes.attributes.push(translation);
      } else if (translation.identifier === MODULE_ITEM_LABEL_IDENTIFIER) {
        labelTranslation = translation;
        translationsAndAttributes.translations.push({
          ...translation,
          translation: this.moduleItemCore.value.label,
        });
      } else {
        translationsAndAttributes.translations.push(translation);
      }
    });

    const translationIdentifiersOrder = [
      MODULE_ITEM_QUESTION_IDENTIFIER,
      MODULE_ITEM_DESCRIPTION_IDENTIFIER,
      MODULE_ITEM_LABEL_IDENTIFIER,
    ];

    translationsAndAttributes.translations?.sort(
      (translation1, translation2) =>
        translationIdentifiersOrder.indexOf(translation1.identifier) -
        translationIdentifiersOrder.indexOf(translation2.identifier)
    );

    return translationsAndAttributes;
  }

  saveModuleItem() {
    this.formSubmitted = true;

    const allTranslations = [...this.moduleItemCore.value.translations, ...this.translations.value];

    if (!this.hideInfo.value && isThereEmptyTranslation(allTranslations, MODULE_ITEM_QUESTION_IDENTIFIER)) {
      this.toastrService.error('Please populate Question for all supported languages');
      return;
    }

    if (this.ivyForm.invalid) {
      return;
    }

    const translationsAndAttributes = this.buildTranslationsAndAttributesForRequest(allTranslations);

    let moduleItem = {
      ...this.ivyForm.value,
      subType: this.moduleItemSubType?.type || this.selectedModuleItem?.subType,
      type: this.moduleItemSubType?.itemType || this.selectedModuleItem?.type,
      moduleId: this.selectedModule?.id,
      isEditing: this.isEdit,
      imagePath: this.moduleItemCore.value.imagePath,
      videoUrl: this.dashboardModuleItemCore.isVideoInputVisible
        ? prepareApiVideoUrl(this.moduleItemCore.value.videoUrl)
        : '',
      translations: translationsAndAttributes.translations,
      attributes: [
        {
          attribute: ModuleItemAvailableAttribute.HIDE_INFO,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: +this.hideInfo.value,
        },
        {
          attribute: ModuleItemAvailableAttribute.TOP_ZONE_GROUP,
          moduleItemId: this.selectedModuleItem?.id || 0,
          translations: translationsAndAttributes.attributes.filter(
            (attribute) => attribute.identifier === ModuleItemAvailableAttribute.TOP_ZONE_GROUP
          ),
        },
        {
          attribute: ModuleItemAvailableAttribute.TOP_ZONE_WORD,
          moduleItemId: this.selectedModuleItem?.id || 0,
          translations: translationsAndAttributes.attributes.filter(
            (attribute) => attribute.identifier === ModuleItemAvailableAttribute.TOP_ZONE_WORD
          ),
        },
        {
          attribute: ModuleItemAvailableAttribute.TOP_ZONE_COLOR,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: this.topZoneColor.value,
        },
        {
          attribute: ModuleItemAvailableAttribute.TOP_ZONE_AUDIO,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: this.defaultTopZoneAudio.value
            ? this.correctZone.value === MODULE_ITEM_IVY_TOP_ZONE
              ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
              : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO
            : this.topZoneAudio.value ===
              (this.correctZone.value === MODULE_ITEM_IVY_TOP_ZONE
                ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO)
            ? ''
            : this.topZoneAudio.value,
        },
        {
          attribute: ModuleItemAvailableAttribute.BOTTOM_ZONE_GROUP,
          moduleItemId: this.selectedModuleItem?.id || 0,
          translations: translationsAndAttributes.attributes.filter(
            (attribute) => attribute.identifier === ModuleItemAvailableAttribute.BOTTOM_ZONE_GROUP
          ),
        },
        {
          attribute: ModuleItemAvailableAttribute.BOTTOM_ZONE_WORD,
          moduleItemId: this.selectedModuleItem?.id || 0,
          translations: translationsAndAttributes.attributes.filter(
            (attribute) => attribute.identifier === ModuleItemAvailableAttribute.BOTTOM_ZONE_WORD
          ),
        },
        {
          attribute: ModuleItemAvailableAttribute.BOTTOM_ZONE_COLOR,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: this.bottomZoneColor.value,
        },
        {
          attribute: ModuleItemAvailableAttribute.BOTTOM_ZONE_AUDIO,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: this.defaultBottomZoneAudio.value
            ? this.correctZone.value === MODULE_ITEM_IVY_BOTTOM_ZONE
              ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
              : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO
            : this.bottomZoneAudio.value ===
              (this.correctZone.value === MODULE_ITEM_IVY_BOTTOM_ZONE
                ? MODULE_ITEM_DEFAULT_IVY_CORRECT_AUDIO
                : MODULE_ITEM_DEFAULT_IVY_WRONG_AUDIO)
            ? ''
            : this.bottomZoneAudio.value,
        },
        {
          attribute: ModuleItemAvailableAttribute.DRAG_WORD,
          moduleItemId: this.selectedModuleItem?.id || 0,
          translations: translationsAndAttributes.attributes.filter(
            (attribute) => attribute.identifier === ModuleItemAvailableAttribute.DRAG_WORD
          ),
        },
        {
          attribute: ModuleItemAvailableAttribute.DRAG_WORD_COLOR,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: this.dragWordColor.value,
        },
        {
          attribute: ModuleItemAvailableAttribute.CORRECT_ZONE,
          moduleItemId: this.selectedModuleItem?.id || 0,
          value: +this.correctZone.value,
        },
      ],
      routing: this.routingService.isRoutingDataChanged()
        ? this.routingService.getRoutingData()
        : this.selectedModuleItem?.routing,
    };

    if (this.selectedModule?.randomization === RANDOMIZATION_VALUES.CATEGORY) {
      if (!this.moduleItemCore.value.categoryId) {
        this.toastrService.error('Please select Category Label');
        return;
      }

      moduleItem = {
        ...moduleItem,
        categoryId: this.moduleItemCore.value.categoryId,
      };
    }

    delete moduleItem.moduleItemCore;
    delete moduleItem.hideInfo;
    delete moduleItem.correctZone;
    delete moduleItem.topZoneColor;
    delete moduleItem.topZoneAudio;
    delete moduleItem.bottomZoneColor;
    delete moduleItem.bottomZoneAudio;
    delete moduleItem.dragWordColor;
    delete moduleItem.defaultBottomZoneAudio;
    delete moduleItem.defaultTopZoneAudio;

    if (moduleItem.routing && moduleItem.routing.routingGroups && !moduleItem.routing.routingGroups.length) {
      delete moduleItem.routing;
    }

    if (this.routingService.isRoutingDataChanged()) {
      this.routingService.resetIsRoutingDataChanged();
    }
    if (this.isEdit && !this.isDuplicate) {
      moduleItem = {
        ...moduleItem,
        id: this.selectedModuleItem?.id,
      };
      this.store.dispatch(
        updateModuleItem({
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
          moduleItem,
        })
      );
    } else {
      this.store.dispatch(
        createModuleItem({
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
          moduleItem,
        })
      );
    }
  }

  closeModuleItemDialog(): void {
    this.moduleItemDialogRef.close();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
